/**
 * ========================================
 * INVOICE CALCULATION SERVICE
 * Zentrale Berechnungslogik für alle Invoice-Typen
 * Money-SSOT: totalAmount / paidAmount (ADR-0013 suppliers/balance)
 * ========================================
 */

import { resolveInvoiceRemainingAmount } from '../domain/suppliers/balance';
import { isConsolidatedInvoiceRecord } from '../domain/purchases/consolidatedInvoiceRefs';

class InvoiceCalculationService {
  constructor() {
    this.cache = new Map();
    this.cacheTimeout = 5 * 60 * 1000; // 5 Minuten Cache
  }

  /**
   * Line items for display (items preferred; products legacy).
   */
  resolveLineItems(invoice) {
    if (Array.isArray(invoice?.items) && invoice.items.length > 0) {
      return invoice.items;
    }
    if (Array.isArray(invoice?.products) && invoice.products.length > 0) {
      return invoice.products;
    }
    return [];
  }

  /**
   * Optional display subtotal from lines — never overrides ledger totalAmount.
   */
  sumLineSubtotal(lines = []) {
    return lines.reduce((sum, line) => {
      const quantity = Number(
        line.quantity ?? line.receivedQuantity ?? 0
      );
      const price = Number(
        line.unitPrice ??
          line.purchaseUnitPrice ??
          line.orderUnitPrice ??
          line.price ??
          0
      );
      const lineTotal = Number(
        line.totalPrice ?? line.total ?? quantity * price
      );
      return sum + (Number.isFinite(lineTotal) ? lineTotal : 0);
    }, 0);
  }

  /**
   * Berechne Invoice-Totals (zentrale Logik).
   * total / paid / remaining immer aus gespeichertem Ledger (wie Payment & Bilan).
   */
  calculateInvoiceTotals(invoice) {
    if (!invoice) {
      return {
        subtotal: 0,
        tax: 0,
        total: 0,
        itemCount: 0,
        paidAmount: 0,
        remainingAmount: 0
      };
    }

    const isConsolidated = isConsolidatedInvoiceRecord(invoice);
    const totalAmount = Number(invoice.totalAmount ?? invoice.total ?? 0) || 0;
    const paidAmount = Number(invoice.paidAmount) || 0;
    const remainingAmount = resolveInvoiceRemainingAmount(invoice);
    const lines = this.resolveLineItems(invoice);
    const lineSubtotal = this.sumLineSubtotal(lines);
    const subtotal =
      lineSubtotal > 0 && !isConsolidated ? lineSubtotal : totalAmount;
    const itemCount = isConsolidated
      ? invoice.originalInvoices?.length || 0
      : lines.length;

    return {
      subtotal: Math.round(subtotal * 100) / 100,
      tax: 0,
      total: Math.round(totalAmount * 100) / 100,
      itemCount,
      paidAmount: Math.round(paidAmount * 100) / 100,
      remainingAmount: Math.round(remainingAmount * 100) / 100
    };
  }

  /**
   * Berechne Invoice-Items (zentrale Logik)
   */
  calculateInvoiceItems(invoice, isRTL = false) {
    if (!invoice) return [];

    // Cache-Check
    const cacheKey = `items_${invoice.id}_${isRTL}`;
    const cached = this.getFromCache(cacheKey);
    if (cached) return cached;

    let items = [];

    // Wenn Invoice bereits Items hat, verwende diese
    if (Array.isArray(invoice.items) && invoice.items.length > 0) {
      items = invoice.items.map(it => ({
        productName: it.productName || it.name || (isRTL ? 'عنصر' : 'Article'),
        sku: it.sku || '',
        quantity: Number(it.quantity || 1),
        unitPrice: Number(it.unitPrice || it.price || 0),
        totalPrice: Number(it.totalPrice || ((it.quantity || 1) * (it.unitPrice || it.price || 0)))
      }));
    }
    // Für konsolidierte Invoices: Erweitere ursprüngliche Invoice-Items
    else if (isConsolidatedInvoiceRecord(invoice)) {
      items = this.calculateConsolidatedInvoiceItems(invoice, isRTL);
    }
    // Fallback: Verwende Invoice-Total als einzelne Zeile
    else {
      items = [{
        productName: (isRTL ? 'فاتورة' : 'Facture') + ' ' + (invoice.invoiceNumber || invoice.id),
        quantity: 1,
        unitPrice: invoice.totalAmount || 0,
        totalPrice: invoice.totalAmount || 0
      }];
    }

    // Cache-Ergebnis
    this.setCache(cacheKey, items);
    return items;
  }

  /**
   * Berechne konsolidierte Invoice-Items
   */
  calculateConsolidatedInvoiceItems(invoice, isRTL = false) {
    if (!Array.isArray(invoice.originalInvoices) || invoice.originalInvoices.length === 0) {
      return [{
        productName: (isRTL ? 'فاتورة' : 'Facture') + ' ' + (invoice.invoiceNumber || invoice.id),
        quantity: 1,
        unitPrice: invoice.totalAmount || 0,
        totalPrice: invoice.totalAmount || 0
      }];
    }

    const items = [];
    
    invoice.originalInvoices.forEach(originalInvoice => {
      if (Array.isArray(originalInvoice.items) && originalInvoice.items.length > 0) {
        originalInvoice.items.forEach(it => {
          items.push({
            productName: it.productName || it.name || (isRTL ? 'عنصر' : 'Article'),
            sku: it.sku || '',
            quantity: Number(it.quantity || 1),
            unitPrice: Number(it.unitPrice || it.price || 0),
            totalPrice: Number(it.totalPrice || ((it.quantity || 1) * (it.unitPrice || it.price || 0)))
          });
        });
      } else {
        // Fallback: eine Zeile pro Achat — display = purchaseId (Achats page)
        const achatId =
          originalInvoice.purchaseId ||
          originalInvoice.originalTransactionId ||
          originalInvoice.invoiceNumber ||
          originalInvoice.id;
        items.push({
          productName: (isRTL ? 'فاتورة' : 'Facture') + ' ' + achatId,
          quantity: 1,
          unitPrice: originalInvoice.amount ?? originalInvoice.totalAmount ?? 0,
          totalPrice: originalInvoice.amount ?? originalInvoice.totalAmount ?? 0
        });
      }
    });

    return items;
  }

  /**
   * Berechne Payment-Status
   */
  calculatePaymentStatus(invoice) {
    if (!invoice) return 'pending';

    const totals = this.calculateInvoiceTotals(invoice);
    const remainingAmount = totals.remainingAmount;

    if (remainingAmount <= 0) {
      return 'paid';
    } else if (totals.paidAmount > 0) {
      return 'partial';
    } else {
      return 'pending';
    }
  }

  /**
   * Berechne Payment-Status mit Text
   */
  getPaymentStatusText(invoice, isRTL = false) {
    const status = this.calculatePaymentStatus(invoice);
    
    const statusMap = {
      'pending': isRTL ? 'معلق' : 'En Attente',
      'partial': isRTL ? 'جزئي' : 'Partiel',
      'paid': isRTL ? 'مدفوع' : 'Payé',
      'overdue': isRTL ? 'متأخر' : 'En Retard'
    };

    return statusMap[status] || statusMap['pending'];
  }

  /**
   * Cache-Funktionen
   */
  getFromCache(key) {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      return cached.data;
    }
    this.cache.delete(key);
    return null;
  }

  setCache(key, data) {
    this.cache.set(key, {
      data,
      timestamp: Date.now()
    });
  }

  clearCache() {
    this.cache.clear();
  }

  /**
   * Cache-Statistiken
   */
  getCacheStats() {
    return {
      size: this.cache.size,
      timeout: this.cacheTimeout
    };
  }
}

// Singleton-Instanz
const invoiceCalculationService = new InvoiceCalculationService();

export default invoiceCalculationService;
